import type { PageBlock, PageBlockType, PageTheme, PageThemePreset } from "@/lib/services/lead-service"

let blockIdCounter = 0

export function generateBlockId(): string {
  blockIdCounter++
  return `block_${Date.now()}_${blockIdCounter}`
}

export type BlockCategory = "cabecalho" | "conteudo" | "social" | "conversao" | "estrutura"

interface BlockTemplate {
  tipo: PageBlockType
  label: string
  icone: string
  descricao: string
  categoria: BlockCategory
  defaultProps: Record<string, any>
}

export const BLOCK_TEMPLATES: BlockTemplate[] = [
  // ─── Cabeçalho ───
  {
    tipo: "badge",
    label: "Selo",
    icone: "selo",
    descricao: "Etiqueta curta acima do título",
    categoria: "cabecalho",
    defaultProps: {
      texto: "Mais vendido",
      estilo: "soft",
      alinhamento: "center",
    },
  },
  {
    tipo: "hero",
    label: "Cabeçalho",
    icone: "cabecalhoPagina",
    descricao: "Título, promessa e sua foto",
    categoria: "cabecalho",
    defaultProps: {
      titulo: "Transforme seu corpo",
      subtitulo: "Treinos personalizados para você",
      mostrar_foto: true,
      alinhamento: "center",
    },
  },

  // ─── Conteúdo ───
  {
    tipo: "texto",
    label: "Texto",
    icone: "texto",
    descricao: "Um parágrafo escrito por você",
    categoria: "conteudo",
    defaultProps: {
      conteudo: "Escreva aqui sobre você, sua experiência e metodologia de trabalho.",
      alinhamento: "left",
      tamanho: "base",
    },
  },
  {
    tipo: "imagem",
    label: "Imagem",
    icone: "imagem",
    descricao: "Uma foto com legenda",
    categoria: "conteudo",
    defaultProps: {
      url: "",
      legenda: "",
      alinhamento: "center",
      arredondado: "lg",
    },
  },
  {
    tipo: "video",
    label: "Vídeo",
    icone: "video",
    descricao: "Do YouTube, do Vimeo ou seu arquivo",
    categoria: "conteudo",
    defaultProps: {
      url: "",
      titulo: "",
    },
  },
  {
    tipo: "beneficios",
    label: "Benefícios",
    icone: "beneficio",
    descricao: "Os motivos para comprar, em cartões",
    categoria: "conteudo",
    defaultProps: {
      titulo: "O que você vai receber",
      itens: [
        { icone: "compass", titulo: "Direção clara", descricao: "Saiba exatamente o que fazer a cada dia." },
        { icone: "refresh", titulo: "Constância", descricao: "Hábitos simples que cabem na sua rotina." },
        { icone: "heart", titulo: "Resultado real", descricao: "Transformação visível em poucas semanas." },
      ],
      colunas: 1,
    },
  },
  {
    tipo: "destaque",
    label: "Destaque",
    icone: "destaque",
    descricao: "Uma frase em caixa, para não passar batido",
    categoria: "conteudo",
    defaultProps: {
      titulo: "Atenção",
      conteudo: "Coloque aqui uma frase que você quer dar destaque para o leitor.",
      estilo: "soft",
    },
  },
  {
    tipo: "depoimentos",
    label: "Depoimentos",
    icone: "depoimento",
    descricao: "O que seus alunos dizem",
    categoria: "conteudo",
    defaultProps: {
      titulo: "O que meus alunos dizem",
      itens: [
        { nome: "Maria S.", texto: "Melhor decisão que tomei!", estrelas: 5 },
        { nome: "João P.", texto: "Resultados incríveis em 3 meses!", estrelas: 5 },
      ],
    },
  },
  {
    tipo: "galeria",
    label: "Galeria",
    icone: "galeria",
    descricao: "Várias fotos lado a lado",
    categoria: "conteudo",
    defaultProps: {
      titulo: "Galeria",
      descricao: "Confira alguns resultados",
      imagens: [],
      colunas: 2,
    },
  },
  {
    tipo: "servicos",
    label: "Serviços",
    icone: "servico",
    descricao: "O que você oferece, em lista",
    categoria: "conteudo",
    defaultProps: {
      titulo: "Meus Serviços",
      itens: [
        { icone: "dumbbell", titulo: "Musculação", descricao: "Treinos focados em hipertrofia" },
        { icone: "heart-pulse", titulo: "Cardio", descricao: "Melhore seu condicionamento" },
        { icone: "salad", titulo: "Nutrição", descricao: "Orientação nutricional completa" },
      ],
    },
  },
  {
    tipo: "faq",
    label: "Perguntas",
    icone: "perguntas",
    descricao: "Dúvidas que abrem e fecham",
    categoria: "conteudo",
    defaultProps: {
      titulo: "Perguntas frequentes",
      itens: [
        { pergunta: "Como funciona?", resposta: "Após a compra, você recebe acesso imediato no app." },
        { pergunta: "Posso cancelar?", resposta: "Sim, sem multa, a qualquer momento." },
      ],
    },
  },
  {
    tipo: "garantia",
    label: "Garantia",
    icone: "garantia",
    descricao: "A promessa de devolver o dinheiro",
    categoria: "conteudo",
    defaultProps: {
      titulo: "Garantia de 7 dias",
      descricao: "Se não gostar, devolvemos 100% do valor.",
      icone: "shield",
    },
  },
  {
    tipo: "html",
    label: "HTML",
    icone: "codigo",
    descricao: "Seu código HTML, com texto, imagem e estilo",
    categoria: "conteudo",
    defaultProps: {
      html: "",
    },
  },

  // ─── Conversão ───
  {
    tipo: "quiz",
    label: "Quiz",
    icone: "quiz",
    descricao: "Perguntas em etapas que levam à compra",
    categoria: "conversao",
    defaultProps: {
      titulo: "Descubra o plano ideal pra você",
      subtitulo: "Responda algumas perguntas rápidas",
      etapas: [
        {
          id: "et_1",
          pergunta: "Qual é o seu principal objetivo?",
          descricao: "",
          imagem_url: "",
          tipo: "unica",
          opcoes: [
            { id: "op_1", texto: "Emagrecer" },
            { id: "op_2", texto: "Ganhar massa muscular" },
            { id: "op_3", texto: "Melhorar condicionamento" },
          ],
        },
        {
          id: "et_2",
          pergunta: "Com que frequência você consegue treinar?",
          descricao: "",
          imagem_url: "",
          tipo: "unica",
          opcoes: [
            { id: "op_1", texto: "1 a 2x por semana" },
            { id: "op_2", texto: "3 a 4x por semana" },
            { id: "op_3", texto: "5x ou mais" },
          ],
        },
      ],
      imagem_final_url: "",
      texto_final: "Tudo certo! Com base nas suas respostas, preparei a melhor opção pra você.",
      acao_final: "botao",
      cta_texto: "Ver minha recomendação",
      cta_link: "#checkout",
      form_titulo: "Receba sua recomendação",
      form_botao: "Quero receber",
      form_sucesso: "Pronto! Em breve entramos em contato.",
      botao_texto: "Continuar",
    },
  },
  {
    tipo: "cta",
    label: "Botão",
    icone: "botaoCta",
    descricao: "Um botão que leva a um link",
    categoria: "conversao",
    defaultProps: {
      texto: "Começar agora",
      link: "#checkout",
      estilo: "primario",
      tamanho: "lg",
    },
  },
  {
    tipo: "checkout",
    label: "Botão de compra",
    icone: "comprar",
    descricao: "O botão que cobra, com o preço",
    categoria: "conversao",
    defaultProps: {
      titulo: "Adquira agora",
      texto_botao: "Quero começar!",
    },
  },
  {
    tipo: "formulario",
    label: "Formulário",
    icone: "formulario",
    descricao: "Pede nome e contato de quem visita",
    categoria: "conversao",
    defaultProps: {
      titulo: "Comece agora!",
      texto_botao: "Quero começar!",
      mensagem_sucesso: "Obrigado! Entraremos em contato em breve.",
      campos: ["nome", "email", "telefone"],
    },
  },

  // ─── Social ───
  {
    tipo: "redes_sociais",
    label: "Redes sociais",
    icone: "redesSociais",
    descricao: "Links do seu Instagram e do resto",
    categoria: "social",
    defaultProps: {
      itens: [
        { rede: "instagram", url: "" },
        { rede: "whatsapp", url: "" },
      ],
    },
  },

  // ─── Estrutura ───
  {
    tipo: "divisor",
    label: "Divisor",
    icone: "divisor",
    descricao: "Uma linha separando dois assuntos",
    categoria: "estrutura",
    defaultProps: {
      estilo: "linha",
      ornamento: "sparkle",
    },
  },
  {
    tipo: "espacamento",
    label: "Espaço",
    icone: "espacamento",
    descricao: "Ar entre uma peça e outra",
    categoria: "estrutura",
    defaultProps: {
      altura: 32,
    },
  },
]

export const BLOCK_CATEGORIES: { id: BlockCategory; label: string }[] = [
  { id: "cabecalho", label: "Cabeçalho" },
  { id: "conteudo", label: "Conteúdo" },
  { id: "conversao", label: "Conversão" },
  { id: "social", label: "Social" },
  { id: "estrutura", label: "Estrutura" },
]

export function criarBloco(tipo: PageBlockType, ordem: number): PageBlock {
  const template = BLOCK_TEMPLATES.find((t) => t.tipo === tipo)
  if (!template) throw new Error(`Tipo de bloco inválido: ${tipo}`)

  return {
    id: generateBlockId(),
    tipo,
    props: { ...template.defaultProps },
    ordem,
  }
}

export function blocoPadrao(): PageBlock[] {
  return [
    criarBloco("hero", 0),
    criarBloco("texto", 1),
    criarBloco("beneficios", 2),
    criarBloco("formulario", 3),
    criarBloco("depoimentos", 4),
    criarBloco("redes_sociais", 5),
  ]
}

// ─── Presets de tema ───

export const TEMA_DEFAULT: PageTheme = {
  preset: "white",
  background: "#FFFFFF",
  foreground: "#0F172A",
  muted: "#64748B",
  primary: "#0F172A",
  primary_fg: "#FFFFFF",
  surface: "#F8FAFC",
  border: "#E2E8F0",
  accent: "#D7F205",
  font_heading: "display",
  font_body: "body",
  radius: "lg",
  container_max: "md",
}

export const TEMA_PRESETS: Record<PageThemePreset, PageTheme> = {
  white: { ...TEMA_DEFAULT },
  lime: {
    preset: "lime",
    background: "#FFFFFF",
    foreground: "#0F172A",
    muted: "#475569",
    primary: "#D7F205",
    primary_fg: "#0F172A",
    surface: "#F4FFD9",
    border: "#E2E8F0",
    accent: "#0F172A",
    font_heading: "display",
    font_body: "body",
    radius: "lg",
    container_max: "md",
  },
  purple: {
    preset: "purple",
    background: "#F3EAFF",
    foreground: "#3B1E5E",
    muted: "#7C5BA8",
    primary: "#A8E055",
    primary_fg: "#1F2937",
    surface: "#FFFFFF",
    border: "#E5D5FA",
    accent: "#7C3AED",
    font_heading: "serif",
    font_body: "body",
    radius: "lg",
    container_max: "md",
  },
  ocean: {
    preset: "ocean",
    background: "#F0F9FF",
    foreground: "#0C4A6E",
    muted: "#0369A1",
    primary: "#0EA5E9",
    primary_fg: "#FFFFFF",
    surface: "#FFFFFF",
    border: "#BAE6FD",
    accent: "#06B6D4",
    font_heading: "display",
    font_body: "body",
    radius: "lg",
    container_max: "md",
  },
  warm: {
    preset: "warm",
    background: "#FFF7ED",
    foreground: "#431407",
    muted: "#9A3412",
    primary: "#EA580C",
    primary_fg: "#FFFFFF",
    surface: "#FFFFFF",
    border: "#FED7AA",
    accent: "#F59E0B",
    font_heading: "serif",
    font_body: "body",
    radius: "lg",
    container_max: "md",
  },
  dark: {
    preset: "dark",
    background: "#1A1A1A",
    foreground: "#F5F5F5",
    muted: "#A3A3A3",
    primary: "#D7F205",
    primary_fg: "#0F172A",
    surface: "#222222",
    border: "#2E2E2E",
    accent: "#D7F205",
    font_heading: "display",
    font_body: "body",
    radius: "lg",
    container_max: "md",
  },
}

export function temaPadrao(): PageTheme {
  return { ...TEMA_DEFAULT }
}
